import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, RouterModule, RouterLinkActive } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { Subject, Observable } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import { ChildAuthService } from '../../services/child-auth.service';
import { combineLatest } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { Child } from '../../interfaces/child.interface';
import { CommonModule } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UpdateService } from '../../services/update.service';
import { VersionService } from '../../services/version.service';
import { ScreenSaverService } from '../../services/screen-saver.service';
import { ChoresService } from '../../services/chores.service';
import { DeleteAllChoresDialogComponent } from '../../shared/components/delete-all-chores-dialog/delete-all-chores-dialog.component';
import { MailService } from '../../services/mail.service';

interface MenuOption {
  icon: string;
  label: string;
  action: () => void;
}

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    RouterLinkActive,
    RouterModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatBottomSheetModule,
    MatBadgeModule,
    MatDividerModule,
    MatTooltipModule
  ],
  providers: [
    MailService
  ],
  templateUrl: './app-layout.component.html',
  styleUrls: ['./app-layout.component.scss']
})
export class AppLayoutComponent implements OnInit, OnDestroy {
  isDarkTheme = false;
  isChildMode = false;
  loggedInChild$: Observable<Child | null>;
  profileImage$: Observable<string | null>;
  isChildLoggedIn$: Observable<boolean>;
  unreadCount$: Observable<number>;

  private destroy$ = new Subject<void>();

  constructor(
    public auth: AuthService,
    public childAuth: ChildAuthService,
    private router: Router,
    private updateService: UpdateService,
    private versionService: VersionService,
    private screenSaverService: ScreenSaverService,
    private choresService: ChoresService,
    private dialog: MatDialog,
    private mailService: MailService
  ) {
    this.childAuth.getAuthState().pipe(
      takeUntil(this.destroy$)
    ).subscribe(state => {
      this.isChildMode = state.isChildMode;
    });
    
    this.loggedInChild$ = this.childAuth.getAuthState().pipe(
      map(state => state.loggedInChild)
    );

    this.isChildLoggedIn$ = this.childAuth.getAuthState().pipe(
      map(state => !!state.loggedInChild)
    );

    // Update profile image based on auth state
    this.profileImage$ = combineLatest([
      this.auth.currentUser$,
      this.childAuth.getAuthState()
    ]).pipe(
      map(([user, childState]) => {
        if (childState.isChildMode && childState.loggedInChild) {
          return childState.loggedInChild.profilePicture || null;
        }
        return user?.photoURL || null;
      })
    );
    
    // Initialize the unread messages count
    this.unreadCount$ = this.mailService.getUnreadCount();
  }

  ngOnInit() {
    // Load theme preference
    const savedTheme = localStorage.getItem('theme');
    this.isDarkTheme = savedTheme === 'dark';
    this.applyTheme();
    // REMOVED: Auto-refresh was causing refresh loops
    // this.refreshApp();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async logout() {
    try {
      await this.auth.logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  }

  toggleTheme() {
    this.isDarkTheme = !this.isDarkTheme;
    localStorage.setItem('theme', this.isDarkTheme ? 'dark' : 'light');
    this.applyTheme();
  }

  applyTheme() {
    document.body.classList.toggle('dark-theme', this.isDarkTheme);
  }

  // Method to refresh the app
  refreshApp() {
    console.log('Manually refreshing the app');
    // Use the version service's manual refresh method
    if (this.versionService) {
      this.versionService.manualRefresh();
    } else {
      // Fallback if version service is not available
      window.location.reload();
    }
  }

  // Method to activate the screen saver
  activateScreenSaver() {
    this.screenSaverService.activateScreenSaver();
  }
  
  // Temporary method to delete all chores for testing purposes
  deleteAllChores() {
    const dialogRef = this.dialog.open(DeleteAllChoresDialogComponent, {
      width: '400px'
    });
    
    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.choresService.deleteAllChores()
          .then(() => {
            console.log('All chores deleted successfully');
          })
          .catch(error => {
            console.error('Error deleting all chores:', error);
          });
      }
    });
  }

  getInitials(name: string): string {
    if (!name) return '';
    
    // Split the name by spaces and get the first character of each part
    const parts = name.split(' ');
    
    if (parts.length === 1) {
      // If it's a single name or email, return the first character
      return name.charAt(0).toUpperCase();
    }
    
    // For multiple parts (first and last name), return first character of first and last parts
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }
  
  navigateToMailbox(event: MouseEvent) {
    console.log('Mail button clicked', event);
    console.log('Current router URL:', this.router.url);
    console.log('Routes configuration:', this.router.config);
    console.log('Attempting to navigate to /mail');
    this.router.navigate(['/mail']).then(success => {
      console.log('Navigation result:', success ? 'successful' : 'failed');
    }).catch(err => {
      console.error('Navigation error:', err);
    });
  }
} 